import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import Modal, { CommonModalProps } from "..";
import FormSection from "../../Forms/FormSection/form-section";
import { useState, forwardRef, useCallback, useMemo, useRef } from "react";
import { assetService, ingestService } from "../../../Core/Api/services";
import { UpdateMetadataFieldsRequest } from "../../../Core/Api/Types/update-metadata-fields-request";
import TableSkeleton from "../../Table/TableSkeleton";
import ModalEditMetadataDatabase from "./modal-edit-metadata-database";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { Licences } from "../../../Core/Api/Types/licences";
import { UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import ContactlessOutlinedIcon from "@mui/icons-material/ContactlessOutlined";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import EditIcon from "@mui/icons-material/Edit";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import MetadataEditor from "../../MetadataEditor/metadata-editor";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { GetMetadataByTagName, IsPermissionGranted } from "../../../Core/Utils/asset-object-utils";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { useTranslation } from "react-i18next";
import UploadCard, { UploadCardHandle, UploadContext } from "../../Cards/UploadCard/upload-card";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { UpdateObjectsMetadataFieldsRequest } from "../../../Core/Api/Types/update-objects-metadata-fields-request";
import useMetadataForm from "../../../Utils/Hooks/useMetadataForm";

export type ModalEditMetadataElementProps = {
  onClose?: (props: CloseClbProps) => void;
  onSuccess?: (props: CloseClbProps) => void;
  objects: DbAssetObjectInfos[];
};
type CloseClbProps = {};

export type ModalEditMetadataProps = CommonModalProps<ModalEditMetadataElementProps>;

const ModalEditMetadata = forwardRef<any, ModalEditMetadataProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onSuccess, objects } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const uploadPhotoRef = useRef<UploadCardHandle>(null);

  const { formValid, handleMetadataChanges, metadataListFilled } = useMetadataForm();

  const userHasRightToUpdate = useMemo(() => {
    if (objects.length === 0) return false;
    objects.forEach((obj) => {
      if (!IsPermissionGranted(obj, PermissionType.MetadataEdit)) return false;
    });
    return true;
  }, [objects]);

  const enqueueCustomSnackbar = useCustomSnackbar();

  // Handlers
  const handleSubmit = useCallback(() => {
    if (metadataListFilled.length === 0) enqueueCustomSnackbar(t("alert:warning.noMetaFilled"), "warning");

    let request: any = null;
    if (objects.length === 1 && metadataListFilled.length > 0 && formValid)
      request = assetService.UpdateMetadataFields(
        new UpdateMetadataFieldsRequest({ objectId: objects[0].id, metadata: metadataListFilled })
      );
    if (objects.length > 1 && metadataListFilled.length > 0 && formValid)
      request = assetService.UpdateObjectsMetadataFields(
        new UpdateObjectsMetadataFieldsRequest({ objectIds: objects.map((x) => x.id), metadata: metadataListFilled })
      );
    if (request) {
      setButtonLoading(true);
      request
        .then((response) => {
          if (response.success) {
            onSuccess && onSuccess({});
            closeModal();
            if (response.messages.length > 0) {
              response.messages.forEach((msg) => {
                enqueueCustomSnackbar(t("alert:warning.oopsMessage") + msg, "warning");
              });
            }
            enqueueCustomSnackbar(t("alert:success.metadataUpdated"), "success");
          } else enqueueCustomSnackbar(t("alert:warning.oopsMessage") + response.message, "warning");
          setButtonLoading(false);
        })
        .catch((error) => {
          setButtonLoading(true);
        });
    }
  }, [metadataListFilled, formValid, objects]);

  const hasMetaUpdateAcl = (obj: DbAssetObjectInfos | undefined): boolean => {
    if (!obj) return false;

    if (obj.layer === ObjectLayer.Title && UserHasAcl(AclTypes.aclMetadataTitleUpdate)) return true;
    if (obj.layer === ObjectLayer.Content && UserHasAcl(AclTypes.aclMetadataContentUpdate)) return true;
    if (
      (obj.layer === ObjectLayer.Asset || obj.layer === ObjectLayer.Clip) &&
      obj.status !== ObjectStatus.Accepted &&
      UserHasAcl(AclTypes.aclMetadataUpdate)
    )
      return true;
    if (
      obj.layer === ObjectLayer.Asset &&
      obj.status === ObjectStatus.Accepted &&
      UserHasAcl(AclTypes.aclMetadataUpdateWithAcceptedAssetStatus)
    )
      return true;

    return false;
  };

  const hasMetadataUpdateAcl: boolean = useMemo(() => {
    if (objects.length === 0) return false;
    objects.forEach((obj) => {
      if (!hasMetaUpdateAcl(obj)) return false;
    });
    return true;
  }, [objects]);

  const onSuccessUpload = () => {
    if (objects.length > 1) return;
    ingestService.ReprocessRequestAfterAutomatedRejection(objects[0].id).then((response) => {
      if (response.success)
        enqueueCustomSnackbar(t("alert:success.assetReprocessSuccess", { assetId: objects[0].id }), "success");
      else enqueueCustomSnackbar(t("alert:error.errorReprocessRequest", { message: response.message }), "error");
    });
  };

  return (
    <Modal
      title={
        <Stack direction="row" spacing={2} alignItems="center">
          <EditIcon />
          <Typography variant="h5">
            {t("modal:body.metadataEditor")} {objects.length === 1 && "- " + objects[0].name}
          </Typography>
        </Stack>
      }
      reference={ref}
      closeModal={closeModal}
      width={1024}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={closeModal} disabled={buttonLoading}>
            {t("common:cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formValid || !userHasRightToUpdate || buttonLoading}
          >
            {buttonLoading ? <CircularProgress size={20} thickness={5} color="secondary" /> : t("button:saveChanges")}
          </Button>
        </Stack>
      }
    >
      {loading ? (
        <TableSkeleton />
      ) : (
        <Stack spacing={3}>
          <>
            {hasMetadataUpdateAcl && objects.length === 1 && (
              <MetadataEditor objectIds={[objects[0].id]} onMetadataChange={handleMetadataChanges} />
            )}

            {hasMetadataUpdateAcl && objects.length > 1 && (
              <MetadataEditor objectIds={objects.map((x) => x.id)} onMetadataChange={handleMetadataChanges} />
            )}

            {objects.length === 1 &&
              objects[0].layer === ObjectLayer.Title &&
              ((UserHasAcl(AclTypes.aclConnectLinkTitle) && UserHasLicence(Licences.ConnectLink)) ||
                UserHasAcl(AclTypes.aclSyncTitleFromSpock)) && (
                <FormSection
                  icon={<ContactlessOutlinedIcon />}
                  title={t("form:section.title.databaseConnections")}
                  status="todo"
                  defaultExpanded
                >
                  <ModalEditMetadataDatabase title={objects[0]} onCloseParent={() => closeModal()} />
                </FormSection>
              )}
          </>
        </Stack>
      )}
      {objects.length === 1 &&
        objects[0].status === ObjectStatus.RejectedAuto &&
        GetMetadataByTagName(objects[0], "isHdr")?.value && (
          <Stack mt={3} pb={2}>
            <UploadCard
              title="Upload Xml"
              userHasRightToDelete={true}
              ref={uploadPhotoRef}
              context={UploadContext.RequestMetadata}
              objectId={objects[0].id}
              uploadButtonInCard={true}
              format={{
                id: 0,
                isUploaded: false,
                maxFileSizeMb: 10,
                name: "Document",
                userHasRighToDelete: true,
                acceptsFiles: {
                  ["application/octet-stream"]: [".xml"],
                },
                description: "test",
                isUserDataMandatory: false,
                objectType: ObjectType.Document,
              }}
              onSuccessUpload={onSuccessUpload}
            />
          </Stack>
        )}
    </Modal>
  );
});

export default ModalEditMetadata;
